import messagesInEnglish from 'app/lang/en.json';
import messagesInBritishEnglish from 'app/lang/en-GB.json';
import messagesInCanadianEnglish from 'app/lang/en-CA.json';
import messagesInIrishEnglish from 'app/lang/en-IE.json';
import messagesInAustralianEnglish from 'app/lang/en-AU.json';
import messagesInGerman from 'app/lang/de-DE.json';
import messagesInSpanish from 'app/lang/es-ES.json';
import messagesInFrench from 'app/lang/fr-FR.json';
import messagesInCanadianFrench from 'app/lang/fr-CA.json';
import messagesInItalian from 'app/lang/it-IT.json';
import messagesInNorwegian from 'app/lang/nb-NO.json';
import messagesInDutch from 'app/lang/nl-NL.json';
import messagesInPolish from 'app/lang/pl.json';
import messagesInPortuguese from 'app/lang/pt-PT.json';
import messagesInHungarian from 'app/lang/hu-HU.json';
import messagesInSlovak from 'app/lang/sk-SK.json';
import { flatten } from 'app/shared/utils/flatten';
import { useMemo } from 'react';

export const DEFAULT_LOCALE = 'en';

// Keys are lower-case hyphenated so the normalized lookup in getAvailableLocale hits regardless of the
// host's casing/separator. Each single-region language also carries a bare-language alias (`de`, `fr`,
// …) so a language-only host tag still resolves instead of falling to English. en-GB is the British
// dialect parent en-IE / en-AU derive from; bare `fr` maps to fr-FR (not fr-CA).
const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.baseMiniApp],
  ['en-gb', messagesInBritishEnglish.baseMiniApp],
  ['en-ca', messagesInCanadianEnglish.baseMiniApp],
  ['en-ie', messagesInIrishEnglish.baseMiniApp],
  ['en-au', messagesInAustralianEnglish.baseMiniApp],
  ['de-de', messagesInGerman.baseMiniApp],
  ['de', messagesInGerman.baseMiniApp],
  ['es-es', messagesInSpanish.baseMiniApp],
  ['es', messagesInSpanish.baseMiniApp],
  ['fr-fr', messagesInFrench.baseMiniApp],
  ['fr', messagesInFrench.baseMiniApp],
  ['fr-ca', messagesInCanadianFrench.baseMiniApp],
  ['it-it', messagesInItalian.baseMiniApp],
  ['it', messagesInItalian.baseMiniApp],
  ['nb-no', messagesInNorwegian.baseMiniApp],
  ['nb', messagesInNorwegian.baseMiniApp],
  ['nl-nl', messagesInDutch.baseMiniApp],
  ['nl', messagesInDutch.baseMiniApp],
  ['pl', messagesInPolish.baseMiniApp],
  ['pt-pt', messagesInPortuguese.baseMiniApp],
  ['pt', messagesInPortuguese.baseMiniApp],
  ['hu-hu', messagesInHungarian.baseMiniApp],
  ['hu', messagesInHungarian.baseMiniApp],
  ['sk-sk', messagesInSlovak.baseMiniApp],
  ['sk', messagesInSlovak.baseMiniApp],
]);

// The API resolves `lang` by exact `xx-XX` match and silently falls back to en_US on anything else — a
// bare tag is not a partial match there, it is a miss. Hosts routinely pass one (`?locale=fr`), and the
// bare aliases above exist precisely because they do, so sending `intl.locale` unchanged would ask for
// French chrome and get an English resource name back. Expand to the region whose catalogue we render.
const API_TAG_BY_BARE_LANGUAGE = new Map<string, string>([
  ['en', 'en-US'],
  ['de', 'de-DE'],
  ['es', 'es-ES'],
  ['fr', 'fr-FR'],
  ['it', 'it-IT'],
  ['nb', 'nb-NO'],
  ['nl', 'nl-NL'],
  ['pl', 'pl-PL'],
  ['pt', 'pt-PT'],
  ['hu', 'hu-HU'],
  ['sk', 'sk-SK'],
]);

/**
 * The locale tag to send to the API, which is NOT always the one we render with: `intl.locale` may be a
 * bare language, and the API only understands `xx-XX`. Regional tags pass through untouched.
 */
export const getApiLocale = (locale: string): string => {
  const normalized = locale.toLowerCase().replace(/_/g, '-');

  return API_TAG_BY_BARE_LANGUAGE.get(normalized) ?? normalized;
};

export const getAvailableLocale = (userLocale: string): [string, string] => {
  // Normalize case + separator before the lookup: map keys are lower-case hyphenated, but a host may
  // pass a canonical ('hu-HU', region-capitalised by Intl.getCanonicalLocales) or underscore ('hu_HU')
  // tag. Matching the raw tag dropped every such value to English (VIS-1695).
  const normalized = userLocale.toLowerCase().replace(/_/g, '-');

  // Return the NORMALIZED tag, not the caller's. It becomes the IntlProvider locale and therefore the
  // argument to every Intl formatter, and an underscored tag is not valid BCP-47 — `hu_HU` throws a
  // RangeError in Intl.NumberFormat / Intl.DateTimeFormat. Handing back the raw input would resolve the
  // catalogue and then crash on the first formatted number, which is worse than the English fallback it
  // replaced. Intl is case-insensitive, so the lower-cased form is safe.
  if (messagesMap.has(normalized)) {
    return [normalized, normalized];
  }

  const [lang] = normalized.split('-');

  if (messagesMap.has(lang)) {
    return [lang, normalized];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData =>
  useMemo(() => {
    const [messagesKey, locale] = getAvailableLocale(userLocale);

    return {
      locale,
      messages: flatten(messagesMap.get(messagesKey)) as Record<string, string>,
    };
  }, [userLocale]);
